// src/features/excursions/components/ExcursionCard.jsx
import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "components/ui/card";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import {
  Edit,
  Trash2,
  Package,
  MoreVertical,
  MapPin,
  Users,
  Bus,
  Ticket,
  Phone,
  CheckCircle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

const getInitials = (name) => {
  if (!name) return "?";
  const names = name.trim().split(/\s+/);
  if (names.length === 0) return "?";
  if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
};

const formatCurrency = (value) => {
  if (!value) return "R$ 0,00";
  if (typeof value === "number") {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  }
  if (typeof value === "string" && value.includes("R$")) return value;
  const numericValue = Number(value);
  if (isNaN(numericValue)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(numericValue);
};

const formatCamarotes = (camaroteData) => {
  if (!camaroteData) return "";

  if (
    camaroteData.numbers &&
    Array.isArray(camaroteData.numbers) &&
    camaroteData.numbers.length > 0
  ) {
    return camaroteData.numbers
      .map((num) => String(num).padStart(2, "0"))
      .join(", ");
  }

  if (camaroteData.number) {
    return String(camaroteData.number).padStart(2, "0");
  }

  return "";
};

function ExcursionCard({
  excursion,
  index,
  onMarkPresent,
  onUpdateLunchInfo,
  onEdit,
  onDelete,
  onUpdateExcursion,
  canEdit,
}) {
  const [localLunchInfo, setLocalLunchInfo] = useState(
    excursion.lunchInfo || ""
  );
  const statusOptions = ["Agendado", "Confirmado", "Cancelado"];

  useEffect(() => {
    setLocalLunchInfo(excursion.lunchInfo || "");
  }, [excursion.lunchInfo]);

  const handleInputBlur = () => {
    if (localLunchInfo !== excursion.lunchInfo) {
      onUpdateLunchInfo(excursion.id, localLunchInfo);
    }
  };

  const currentStatus = excursion.status ? excursion.status.trim() : "";
  const isGreenState =
    currentStatus === "Confirmado" || excursion.presenceStatus === "Presente";

  let cardStyleClass = "border-l-4 border-l-transparent bg-card";

  if (isGreenState) {
    cardStyleClass =
      "border-l-4 border-l-green-500 bg-green-50/30 dark:bg-green-900/10";
  } else if (currentStatus === "Cancelado") {
    cardStyleClass = "border-l-4 border-l-red-500 bg-red-50/10 opacity-75";
  }

  const camaroteDisplay = formatCamarotes(excursion.camarote);

  return (
    <Card
      className={`relative overflow-hidden ${cardStyleClass} transition-all`}
    >
      <div className="absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs">
        {index + 1}
      </div>

      <CardHeader className="flex flex-row items-start gap-3 p-4 pb-2">
        <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold border border-primary/20 shadow-sm flex-shrink-0">
          {getInitials(excursion.clientName)}
        </div>

        <div className="flex-grow min-w-0 pr-6">
          <CardTitle className="text-base font-bold truncate leading-tight">
            {excursion.clientName}
          </CardTitle>
          {excursion.clientPhone && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <Phone className="h-3 w-3" />
              {excursion.clientPhone}
            </div>
          )}
        </div>

        {canEdit && (
          <div className="absolute top-2 right-10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="mr-2 h-4 w-4" /> Editar
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </CardHeader>

      <CardContent className="p-4 pt-2 space-y-4 text-sm">
        {/* Linha 1 */}
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Origem
            </span>
            <span className="font-medium text-gray-800 dark:text-gray-200">
              {excursion.city || "-"}
            </span>
            {excursion.comboIncluded && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 w-fit">
                <Package className="h-3 w-3" /> Pacote
              </span>
            )}
          </div>

          <div className="flex flex-col items-end gap-1 text-right">
            <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider flex items-center gap-1">
              <Users className="h-3 w-3" /> Qtd
            </span>
            <span className="font-medium text-base">
              {excursion.quantity || 0}
            </span>
            {excursion.vehicle && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1 bg-muted px-1.5 py-0.5 rounded">
                <Bus className="h-3 w-3" /> {excursion.vehicle}
              </span>
            )}
          </div>
        </div>

        {/* Linha 2 */}
        <div className="flex justify-between items-start border-t pt-3 border-border/50">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider flex items-center gap-1">
              <Ticket className="h-3 w-3" /> Camarote
            </span>
            {excursion.camarote?.requested ? (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-amber-700 dark:text-amber-400 font-bold text-xs bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded">
                  Sim
                </span>
                {camaroteDisplay && (
                  <span className="text-xs font-medium">
                    Nº {camaroteDisplay}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>

          <div className="flex flex-col items-end gap-1 text-right">
            <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
              Pagamento
            </span>
            <span
              className={`font-bold font-mono ${
                excursion.paymentInfo
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-muted-foreground"
              }`}
            >
              {formatCurrency(excursion.paymentInfo)}
            </span>
          </div>
        </div>

        {/* Linha 3 */}
        <div>
          <Input
            type="text"
            value={localLunchInfo}
            onChange={(e) => setLocalLunchInfo(e.target.value)}
            onBlur={handleInputBlur}
            placeholder="Observações / Almoço..."
            className="h-9 text-xs bg-secondary/40 border border-input/20 hover:bg-secondary/60 focus:bg-background focus:border-primary transition-all placeholder:text-muted-foreground/50"
            disabled={!canEdit}
          />
        </div>
      </CardContent>

      <CardFooter className="bg-muted/40 p-3 flex items-center justify-between border-t">
        {excursion.presenceStatus === "Presente" ? (
          <span className="w-full text-center inline-flex items-center justify-center gap-2 px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 ring-1 ring-inset ring-green-600/20">
            <span className="w-2 h-2 rounded-full bg-green-600 animate-pulse"></span>
            Presente
          </span>
        ) : (
          <div className="flex gap-2 w-full">
            <Select
              value={currentStatus}
              onValueChange={(newStatus) =>
                onUpdateExcursion(excursion.id, { status: newStatus })
              }
              disabled={!canEdit}
            >
              <SelectTrigger className="h-9 text-xs flex-grow bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canEdit && (
              <Button
                onClick={() => onMarkPresent(excursion.id)}
                size="sm"
                className="flex-shrink-0 h-9 text-xs gap-1.5"
                variant="outline"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Marcar
              </Button>
            )}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}

export default ExcursionCard;
